import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit";
import { AdminStats } from "@/components/admin/AdminStats";
import { ImportAccount } from "@/components/admin/ImportAccount";
import { TestCleanup } from "@/components/admin/TestCleanup";
import { SupporterActivity } from "@/components/admin/SupporterActivity";
import { GrowthChart } from "@/components/admin/GrowthChart";
import { RefundRequests } from "@/components/admin/RefundRequests";
import { PaymentReview } from "@/components/admin/PaymentReview";
import { Switch } from "@/components/ui/switch";
import { PaidBadge } from "@/components/admin/PaidBadge";
import { ToolsStats } from "@/components/admin/ToolsStats";
import { AiUsage } from "@/components/admin/AiUsage";

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [includeTest, setIncludeTest] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", has_paid: true });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const update = async (u, patch) => { try { await api.put(`/admin/users/${u.id}?${new URLSearchParams(patch)}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await api.delete(`/admin/users/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email}`);
    if (!pw) return;
    try { await api.post(`/admin/users/${u.id}/reset-password`, { new_password: pw }); toast.success("Password reset"); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", form); toast.success("User created"); setForm({ name: "", email: "", password: "", has_paid: true }); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader title="Admin" subtitle={`${users.length} users`} />
      <label className="flex items-center gap-2 text-sm text-mm-muted mb-4 w-fit cursor-pointer" data-testid="admin-include-test">
        <Switch checked={includeTest} onCheckedChange={setIncludeTest} data-testid="admin-include-test-switch" />
        Include test data <span className="text-xs">(staff accounts, test email rules, sandbox payments)</span>
      </label>
      <AdminStats refreshKey={users.length} includeTest={includeTest} />
      <GrowthChart />
      <ToolsStats />
      <AiUsage />
      <PaymentReview onChange={load} />
      <RefundRequests onChange={load} />
      <ImportAccount />
      <form onSubmit={create} className="mm-card p-5 grid sm:grid-cols-5 gap-2 items-center">
        <input className="mm-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-new-name" />
        <input className="mm-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-new-email" />
        <input className="mm-input" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-new-password" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.has_paid} onCheckedChange={(v) => setForm({ ...form, has_paid: v })} />Paid</label>
        <button className="mm-btn" data-testid="admin-create-user"><UserPlus size={16} />Create</button>
      </form>
      <div className="mm-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-mm-muted border-b"><th className="p-4">User</th><th className="p-4">Role</th><th className="p-4">Paid</th><th className="p-4">Active</th><th className="p-4" /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b last:border-0" data-testid={`admin-user-${u.id}`}>
                <td className="p-4"><div className="font-semibold">{u.name}</div><div className="text-mm-muted text-xs">{u.email}</div></td>
                <td className="p-4">
                  <select className="mm-input !py-1.5 !w-28" value={u.role} disabled={u.id === me.id} onChange={(e) => update(u, { role: e.target.value })} data-testid={`admin-role-${u.id}`}>
                    <option value="user">user</option><option value="admin">admin</option>
                  </select>
                </td>
                <td className="p-4"><div className="flex items-center gap-2"><Switch checked={!!u.has_paid} onCheckedChange={(v) => update(u, { has_paid: v })} data-testid={`admin-paid-${u.id}`} /><PaidBadge info={u.paid_info} userId={u.id} /></div></td>
                <td className="p-4"><Switch checked={!!u.is_active} disabled={u.id === me.id} onCheckedChange={(v) => update(u, { is_active: v })} data-testid={`admin-active-${u.id}`} /></td>
                <td className="p-4 whitespace-nowrap">
                  <button className="p-2 text-mm-muted hover:text-mm-primary" onClick={() => resetPw(u)} data-testid={`admin-reset-${u.id}`}><KeyRound size={16} /></button>
                  {u.id !== me.id && <button className="p-2 text-mm-muted hover:text-mm-error" onClick={() => remove(u)} data-testid={`admin-delete-${u.id}`}><Trash2 size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SupporterActivity includeTest={includeTest} />
      <TestCleanup onDone={load} />
    </div>
  );
}
